// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { faPencilAlt, faSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Components
import { FindestTextBox } from "Components";
// Constants
import { HubConstants } from "Constants";
// Contexts
import { WebsocketContext } from "Providers";
// styles
import styles from "./findestEditableSpan.module.scss";
// Enums
import { ToastTypeEnum } from "Enums";
// Helpers
import { ToastHelperSingleton } from "Helpers";

type TFindestEditableSpan = {
    text: string,
    title?: string,
    entityId: number | string,
    extraClassName?: string,
    isQueryTabTitleClassName?: string,
    isQueryTabChangeTitleClassName?: string,
    onChangeText?: (changedText: string) => void,
    doAutoFocus?: boolean,
    forceIsEditing?: boolean,
    onEditStatusChange?: (editStatus: boolean) => void,
    // TODO: Find a better solution for this
    doIgnoreTextUpdates?: boolean,
    doNotDisplayEditPencil?: boolean,
    resourceName?: string,
    isEditOnDoubleClickDisabled?: boolean,
    onEnter?: (text: string, inputRef: HTMLInputElement) => void
    isEditableTechnologyTitle?: boolean, // TODO: check why these names are so specific
    isEditableRequirementTitle?: boolean, // TODO: check why these names are so specific
    isQueryTab?: boolean
}

export const FindestEditableSpan: FC<TFindestEditableSpan> = ({text, title, entityId, extraClassName, isQueryTabTitleClassName, isQueryTabChangeTitleClassName, onChangeText, forceIsEditing,
        onEditStatusChange, onEnter, isEditableTechnologyTitle, isEditableRequirementTitle, resourceName, doNotDisplayEditPencil,
        isEditOnDoubleClickDisabled, doIgnoreTextUpdates, isQueryTab, doAutoFocus}: TFindestEditableSpan) => {
    const [isEditing, setIsEditing] = useState(forceIsEditing !== undefined ? forceIsEditing : false);
    const [currentText, setCurrentText] = useState<string>(text);
    const [currentEntityId, setCurrentEntityId] = useState<number | string>(entityId);

    // Contexts
    const { webSocketController } = useContext(WebsocketContext);

    // Logic
    useEffect(() => {
        if(!doIgnoreTextUpdates) setCurrentText(text);
        if (currentEntityId !== entityId) {
            setIsEditing(false);
            setCurrentEntityId(entityId);
        }
        if(!resourceName) {
            if(forceIsEditing !== undefined && forceIsEditing !== isEditing) {
                setIsEditing(forceIsEditing);
            }
        }
    }, [currentEntityId, isEditing, resourceName, forceIsEditing, entityId, text, doIgnoreTextUpdates]);

    
    const onEditableTextChange = (text: string) => {
        setCurrentText(text);
        if(onChangeText) onChangeText(text);
    };
    
    const receivedEditStatusChange = useCallback((newIsEditingValue: boolean) => {
        setIsEditing(newIsEditingValue);
        if(onEditStatusChange) onEditStatusChange(newIsEditingValue);
    }, [onEditStatusChange]);

    const receivedAlreadyBeingEdited = () => {
        ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Somebody is already editing this field.");
    };

    
    useEffect(() => {
        if(resourceName) {
            webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX}${resourceName}`, receivedEditStatusChange);
            webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${resourceName}`, receivedAlreadyBeingEdited);

            // on clean up time, send stop claiming edit message to prevent claiming lock 
            // and remove handlers
            return () => {
                webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, resourceName);
                webSocketController.removeHandler(`${HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX}${resourceName}`, receivedEditStatusChange);
                webSocketController.removeHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${resourceName}`, receivedAlreadyBeingEdited);
            };
        }
    }, [receivedEditStatusChange, resourceName, webSocketController]);

    const changeIsEditing = (doStartEditing: boolean) => {
        if(resourceName) {
            if(doStartEditing) {
                webSocketController.invokeFunction(HubConstants.START_EDITING_DELIVERY_RESOURCE, resourceName);
            } else {
                webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, resourceName);
            }
        } else {
            setIsEditing(doStartEditing);
            if(onEditStatusChange) onEditStatusChange(doStartEditing);
        }
    };

    return (
        <>
            {isEditing ?
                <>
                    <FindestTextBox isEditableTechnologyTitle={isEditableTechnologyTitle} isEditableRequirementTitle={isEditableRequirementTitle} 
                        value={currentText} onChange={onEditableTextChange} doAutoFocus={doAutoFocus} 
                        onEnter={onEnter}/>
                    <button className={[styles.changeContentButton, styles.exit, isQueryTabChangeTitleClassName ? isQueryTabChangeTitleClassName : ""].join(" ")} title={"Exit edit mode"} onClick={() => { changeIsEditing(false); } }>
                        <FontAwesomeIcon icon={faPencilAlt} />
                        <FontAwesomeIcon className={styles.slash} icon={faSlash} />
                        <FontAwesomeIcon className={styles.slashBottom} icon={faSlash} />
                    </button>
                </>
            :
                <>
                    <span style={{margin: "0 0.5rem"}} title={ title ? title : ""} className={isQueryTabTitleClassName ? isQueryTabTitleClassName : ""} onDoubleClick={() => { if(!isEditOnDoubleClickDisabled) changeIsEditing(true); } }>{currentText}</span>
                    {doNotDisplayEditPencil ? null : 
                        <button className={[styles.changeContentButton, styles.edit, extraClassName ? extraClassName : ""].join(" ")} title={"Edit mode"} onClick={() => { changeIsEditing(true); } }>
                            <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                    }
                </>
            }
        </>
    );
};
